/**
 * Vistas: funciones puras que devuelven HTML a partir del estado (como los `body` de SwiftUI). Los botones llevan
 * `data-act` (y `data-arg`) y main.ts reparte los toques. Los valores que cambian cada instante (relojes, barra de
 * progreso) van en elementos `data-bind` que se actualizan sin repintar la pantalla.
 */
import {
  addDays, calibrationProgress, choiceLabel, CONTROL_CHOICES, dateKey, decodeFeedback, formLevel, formName, habitStats, isReviewDue,
  minimumDays, questionOf, questionText, quantity, remainingNormals, RIR_CHOICES, summaryText, WING_CHUN_FORMS,
  type Exercise, type Measure, type MeasurementCode, type Section,
} from '@sut/domain';
import type { Session, Workbook } from '@sut/workbook';
import type { Engine } from '../app/engine.js';
import { CELEBRATION_SOUNDS, type Prefs } from '../app/prefs.js';
import { OUTCOMES, wingChunToday, type PracticeResult, type WingChunSource } from '../app/wingchun.js';
import { blockSummary, formAggregates, hasIncompleteGuidedRound, measuredExercises, totalText, wingChunReviewProgress } from '../app/review.js';
import { recoveryPercent, type HabitStats, type WorkoutConfig } from '@sut/domain';
import type { WingChunPractice } from '@sut/workbook';
import { icons } from './icons.js';

export const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
export const clock = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const btn = (label: string, act: string, cls = 'primary', arg = '', extra = '') =>
  `<button class="${cls}" data-act="${act}"${arg ? ` data-arg="${esc(arg)}"` : ''}${extra}>${label}</button>`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const shortDate = (key: string) => { const [, m, d] = key.split('-'); return `${d}/${m}`; };

export interface HomeUi { fuerzaExpanded: boolean; wingChunExpanded: boolean }

export interface SyncInfo { syncing: boolean; pending: number; conflicts: string[]; message: string; reauth: string; isDev: boolean }

// ---------------------------------------------------------------------------------------------------------------
// Home

export function homeView(wb: Workbook, engine: Engine, ui: HomeUi, sync: SyncInfo): string {
  const wc = wingChunToday(wb);
  return `<div class="screen home">
    ${header(sync.isDev)}
    ${sync.reauth ? `<div class="chip warn">${icons.warning}<span>${esc(sync.reauth)} <a href="/api/auth/google/start">Volver a dar permiso</a></span></div>` : ''}
    ${syncChip(sync)}
    ${weekCard(wb)}
    ${blockCard(wb)}
    ${fuerzaCard(engine, wb, ui)}
    ${wingChunCard(wb, wc, ui)}
    ${btn(`${icons.history} Ver historial`, 'openHistory', 'secondary')}
    <p class="principle">No preguntes “¿cuánto puedo hacer hoy?”.<br>Pregunta: “¿qué puedo hacer hoy para querer volver mañana?”</p>
  </div>`;
}

function header(isDev: boolean) {
  return `<header class="home-header">
    <div><p class="eyebrow">${isDev ? '<span class="dev">DEV</span> ' : ''}SISTEMA DIARIO</p>
      <h1>Vuelve mañana</h1><p class="muted">Hoy solo tienes que aparecer.</p></div>
    <button class="icon-button" data-act="openSettings" aria-label="Ajustes">${icons.gear}</button>
  </header>`;
}

function syncChip(s: SyncInfo) {
  const errors = [...s.conflicts, ...(s.message ? [s.message] : [])];
  if (errors.length) return `<button class="chip warn" data-act="syncNow">${icons.warning}<span>${esc(errors[0])}${errors.length > 1 ? ` (+${errors.length - 1})` : ''} · Reintentar</span></button>`;
  if (s.pending) return `<button class="chip quiet" data-act="syncNow">${icons.sync}<span>${s.syncing ? 'Guardando en el libro…' : `${plural(s.pending, 'cambio', 'cambios')} sin guardar en el libro · Reintentar`}</span></button>`;
  return '';
}

/** «Últimos 7 días»: un fallo es un hueco, no una vuelta a cero. Hoy pendiente = punto con borde (una invitación). */
function weekCard(wb: Workbook) {
  const today = dateKey();
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const fuerza = new Set(wb.sessions.map(s => s.date).filter(Boolean));
  const wc = minimumDays(wb.wingChun);
  const initial = (k: string) => { const [y, m, d] = k.split('-').map(Number) as [number, number, number]; return 'DLMXJVS'[new Date(y, m - 1, d, 12).getDay()]; };
  const row = (title: string, done: Set<string>) => {
    const streak = habitStats(done, today).currentStreak;
    return `<div class="week-row" aria-label="${title}: ${days.filter(d => done.has(d)).length} de 7 días">
      <span class="week-label">${title}</span>
      <span class="dots">${days.map(d => `<i class="dot${done.has(d) ? ' done' : ''}${d === today && !done.has(d) ? ' today' : ''}"></i>`).join('')}</span>
      <span class="week-streak">${streak > 0 ? `racha ${streak}` : ''}</span></div>`;
  };
  return `<section class="card">
    <p class="eyebrow">ÚLTIMOS 7 DÍAS</p>
    ${row('Fuerza', fuerza)}${row('Wing Chun', wc)}
    <div class="week-row days" aria-hidden="true"><span class="week-label"></span>
      <span class="dots">${days.map(d => `<i class="${d === today ? 'is-today' : ''}">${initial(d)}</i>`).join('')}</span><span class="week-streak"></span></div>
  </section>`;
}

export function blockProgress(wb: Workbook) {
  const normals = wb.sessions.filter(s => s.sessionType === 'NORMAL' && s.normalFinishedAt).length;
  const n = (k: string, d: number) => { const v = Number(wb.settings[k]); return Number.isFinite(v) && wb.settings[k] !== '' && wb.settings[k] !== undefined ? v : d; };
  return calibrationProgress(normals, n('CALIBRATION_BASE_NORMAL_COUNT', 0), n('CALIBRATION_TARGET_NORMALS', 4), n('CALIBRATION_BLOCK_NUMBER', 1));
}

function blockCard(wb: Workbook) {
  const p = blockProgress(wb);
  const due = isReviewDue(p);
  const left = remainingNormals(p);
  return `<section class="card">
    <div class="row"><p class="eyebrow">BLOQUE ${p.blockNumber}</p><b class="num">${p.completed}/${p.target}</b></div>
    <div class="block-dots">${Array.from({ length: p.target }, (_, i) => `<i class="${i < p.completed ? 'on' : ''}"></i>`).join('')}</div>
    <p class="muted">${due ? 'Bloque completado. Toca revisar esfuerzo, recuperación y ganas de volver con ChatGPT.'
      : left === 1 ? 'Falta 1 sesión normal para la revisión con ChatGPT.' : `Faltan ${left} sesiones normales para la revisión con ChatGPT.`}</p>
    ${due ? btn('Preparar texto para ChatGPT', 'openBlockReview') : ''}
  </section>`;
}

function activityHeader(kind: 'fuerza' | 'wingChun', title: string, done: boolean, expandable: boolean, expanded = false, act = '') {
  const inner = `<span class="activity-icon">${kind === 'fuerza' ? icons.pushup : icons.martialArts}</span>
    <span class="activity-title">${title}</span>
    ${done ? `<span class="status done">${icons.sealCheck}Cumplido</span>` : '<span class="status">Pendiente</span>'}
    ${expandable ? `<span class="chevron${expanded ? ' open' : ''}">${icons.chevronDown}</span>` : ''}`;
  return expandable ? `<button class="activity-header" data-act="${act}" aria-expanded="${expanded}">${inner}</button>` : `<div class="activity-header">${inner}</div>`;
}

const exerciseRows = (list: Exercise[]) =>
  `<div class="rows">${list.map(e => `<div class="ex-row"><span>${esc(e.name)}</span><b class="num">${esc(quantity(e))}</b></div>`).join('')}</div>`;

function fuerzaCard(engine: Engine, wb: Workbook, ui: HomeUi): string {
  const c = engine.config;
  const s = engine.session;
  switch (engine.phase) {
    case 'idle':
    case 'floor': {
      const start = engine.phase === 'idle';
      return `<section class="card">
        ${activityHeader('fuerza', 'Fuerza', false, true, ui.fuerzaExpanded, 'toggleFuerza')}
        ${ui.fuerzaExpanded ? `<p class="muted">Hazlo aunque hoy no hagas nada más.</p>${exerciseRows(c.floor)}`
          : `<p class="muted">Rutina diaria · ${plural(c.floor.length, 'ejercicio', 'ejercicios')}</p>`}
        ${start ? btn('Empezar', 'beginDay') : btn('He hecho el suelo', 'completeFloor')}
      </section>`;
    }
    case 'warmup':
      return `<section class="card">
        <div class="activity-header"><span class="activity-icon flame">${icons.flame}</span><span class="activity-title">Calentamiento</span></div>
        <p class="muted">A veces ya llegas caliente (andar rápido, correr). Si es así, sáltalo.</p>
        <p class="big-clock" data-bind="warmup">00:00</p>
        ${engine.warmupRunning ? btn('Terminar calentamiento', 'finishWarmup') : btn('Empezar calentamiento', 'startWarmup') + btn('Saltar', 'skipWarmup', 'secondary')}
      </section>`;
    case 'offer': {
      const wc = wingChunToday(wb);
      const form = wc.suggested;
      const avg = wc.average(form);
      return `<section class="card">
        <div class="activity-header"><span class="activity-icon">${icons.martialArts}</span><span class="activity-title">${esc(formName(form))}</span></div>
        <p class="muted">¿La practicas ahora, antes del entreno normal?</p>
        <p class="muted small">Nivel ${formLevel(form)}${avg ? ` · tu media ${clock(avg)}` : ''}</p>
        ${wc.isDone ? `<p class="ok small">${icons.check} Ya cumpliste el mínimo de Wing Chun hoy · puedes practicar igualmente</p>` : ''}
        ${btn('Empezar forma', 'practice', 'primary', `${form}|strengthFlow`)}
        ${btn('Ahora no', 'proceedFromOffer', 'secondary')}
        ${btn('Hoy prefiero otra', 'pickForm', 'link', 'strengthFlow')}
      </section>`;
    }
    case 'readiness':
    case 'countdown':
      return `<section class="card">
        <p class="ok head">${icons.checkCircle} Suelo completado</p>
        <h2>¿Cómo estás hoy?</h2>
        <p class="muted">Si estás recuperado, puedes continuar. Si no, terminar aquí es cumplir perfectamente el sistema. Si hoy haces boulder fuerte: suelo y punto.</p>
        ${btn('Estoy verde → seguir', 'startNormal')}
        ${btn('Terminar aquí', 'finishFloorOnly', 'secondary')}
      </section>`;
    case 'celebration':
    case 'feedback':
      return `<section class="card">
        <p class="ok head">${icons.checkCircle} Entrenamiento terminado</p>
        <p class="muted">Falta guardar cómo te ha sentado.</p>
        ${btn('Cerrar el día', 'openFeedback')}
      </section>`;
    case 'doneFloorOnly':
      return `<section class="card">
        ${activityHeader('fuerza', 'Fuerza', true, false)}
        <p class="muted">Suelo hecho. Has aparecido, que es lo que cuenta. Si te animas, aún puedes hacer el entreno normal.</p>
        ${btn('Seguir con el entreno normal', 'startNormal')}
        ${s ? btn('Editar día', 'editSession', 'secondary', s.id) : ''}
      </section>`;
    case 'finished':
    case 'normalActive':
      return `<section class="card">
        ${activityHeader('fuerza', 'Fuerza', true, false)}
        ${s?.normalStartedAt ? `<p class="strong">Entrenamiento normal</p>
          <p class="muted">${plural(s.rounds, 'ronda', 'rondas')} · ${clock(s.durationSeconds)}</p>${feedbackSummary(s, c.normal)}
          ${engine.maxTimeReached ? `<p class="muted small">Techo de ${c.ceilingSeconds / 60} min alcanzado.</p>` : ''}`
          : '<p class="strong">Suelo</p><p class="muted">El mínimo cuenta.</p>'}
        <p class="tomorrow">Vuelve mañana.</p>
        ${s ? btn('Editar día', 'editSession', 'secondary', s.id) : ''}
      </section>`;
  }
}

function feedbackSummary(s: Session, normal: Exercise[]): string {
  const answers = Object.entries(decodeFeedback(s.exerciseFeedback));
  return answers.length ? `<p class="muted small">Última ronda: ${answers.map(([id, code]) => `${esc(normal.find(e => e.id === id)?.name ?? id)} ${esc(summaryText(code))}`).join(' · ')}</p>` : '';
}

function wingChunCard(wb: Workbook, wc: ReturnType<typeof wingChunToday>, ui: HomeUi): string {
  if (wc.isDone) {
    return `<section class="card">
      ${activityHeader('wingChun', 'Wing Chun', true, true, ui.wingChunExpanded, 'toggleWingChun')}
      <p class="muted">${esc(formName(wc.minimumForm ?? 'siu_nim_tau'))} completada.</p>
      ${ui.wingChunExpanded ? (() => {
        const r = wingChunReviewProgress(wb);
        return `${wc.extras.length ? `<p class="muted small">+ ${wc.extras.map(f => esc(formName(f))).join(' · ')}</p>` : ''}
          <p class="small ${r.isDue ? 'warn' : 'muted'}">${r.isDue ? 'Revisión lista con ChatGPT' : `Revisión: ${r.elapsed}/${r.target} días`}</p>
          ${r.isDue ? btn('Copiar revisión', 'copyWingChunReview', 'secondary') : ''}`;
      })() : ''}
      ${btn('Quiero practicar algo más', 'pickForm', 'secondary', 'extraPractice')}
    </section>`;
  }
  const avg = wc.average(wc.suggested);
  return `<section class="card">
    ${activityHeader('wingChun', 'Wing Chun', false, false)}
    <p class="muted">Hoy solo tienes que hacer una forma.</p>
    <p class="muted small">Hoy toca: <b>${esc(formName(wc.suggested))}</b> · Nivel ${formLevel(wc.suggested)}${avg ? ` · tu media ${clock(avg)}` : ''}</p>
    ${btn('Empezar forma', 'practice', 'primary', `${wc.suggested}|wingChunCard`)}
    ${btn('Marcar hecha', 'quickMark', 'secondary', wc.suggested)}
    ${btn('Hoy prefiero otra', 'pickForm', 'link', 'wingChunCard')}
  </section>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Entreno (rondas)

export function workoutView(engine: Engine, prefs: Prefs, pendingAnswer: MeasurementCode | null): string {
  const c = engine.config;
  const guided = engine.isGuidedRound;
  return `<div class="screen workout">
    <p class="eyebrow center">TIEMPO · MÁX ${c.ceilingSeconds / 60} MIN</p>
    <div class="progress"><i data-bind="progress"></i></div>
    <div class="timers">
      <div><p class="eyebrow">LLEVAS</p><p class="timer" data-bind="elapsed">00:00</p></div>
      <div class="right"><p class="eyebrow">QUEDAN</p><p class="timer" data-bind="remaining">00:00</p></div>
    </div>
    ${guided ? guidedView(engine, pendingAnswer) : `
      <h2 class="center">Ronda ${engine.currentRound} de ${c.maxRounds}</h2>
      <section class="card workout-rows" style="--scale:${prefs.textScale}">
        ${c.normal.map(e => `<div class="ex-row"><span>${esc(e.name)}</span><b class="num">${esc(quantity(e))}</b></div>`).join('')}
      </section>
      <div class="spacer"></div>
      ${btn('Ronda completada', 'completeRound', 'primary huge', '', engine.isPaused ? ' disabled' : '')}`}
    <div class="pair">
      ${btn(engine.isPaused ? `${icons.play} Reanudar` : `${icons.pause} Pausa`, engine.isPaused ? 'resume' : 'pause', 'secondary')}
      ${btn('Finalizar antes', 'finishEarly', 'secondary')}
    </div>
  </div>`;
}

function guidedView(engine: Engine, pending: MeasurementCode | null): string {
  const ex = engine.guidedExercise;
  if (!ex) return '';
  const c = engine.config;
  const answers = engine.answers;
  const current = pending ?? answers[ex.id] ?? null;
  const prev = engine.previousExercise;
  const next = engine.nextExercise;
  const q = questionOf(ex);
  const choice = (code: MeasurementCode, cls = '') =>
    `<button class="choice${cls}${current === code ? ' selected' : ''}" data-act="answer" data-arg="${code}"${engine.isPaused || pending ? ' disabled' : ''}>${esc(choiceLabel(code))}</button>`;
  return `<div class="guided${engine.isPaused ? ' paused' : ''}">
    <p class="strong center">Ronda ${engine.currentRound} de ${c.maxRounds} · Ejercicio ${engine.step + 1} de ${c.normal.length}</p>
    ${prev ? `<button class="prev-chip" data-act="revise">${icons.chevronLeft}${esc(prev.name)}${answers[prev.id] ? ` · ${esc(summaryText(answers[prev.id]!))}` : ''} · <span class="accent">Cambiar</span></button>` : ''}
    <section class="card">
      <div class="guided-ex"><span>${esc(ex.name)}</span><b class="num">${esc(quantity(ex))}</b></div>
      <p class="muted">${next ? `Siguiente: ${esc(next.name)}` : 'Último ejercicio: al responder se completa la ronda.'}</p>
    </section>
    <p class="eyebrow">PRIMERO HAZLO · DESPUÉS RESPONDE</p>
    <p class="strong">${esc(questionText(ex))}</p>
    ${q === 'rir' && ex.prompt ? `<p class="hint">${esc(ex.prompt)}</p>` : ''}
    <div class="spacer"></div>
    ${q === 'done' ? `<button class="primary${current === 'done' ? ' selected' : ''}" data-act="answer" data-arg="done"${engine.isPaused || pending ? ' disabled' : ''}>Hecho</button>`
      : `<div class="choices">${(q === 'rir' ? RIR_CHOICES : CONTROL_CHOICES).map(code => choice(code)).join('')}</div>${choice('unknown', ' wide')}`}
    <button class="link muted" data-act="answer" data-arg="skipped"${engine.isPaused || pending ? ' disabled' : ''}>Saltar ejercicio · no lo he hecho</button>
  </div>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Superposiciones

export function countdownView(value: number | 'go'): string {
  return `<div class="overlay countdown" data-act="skipCountdown">
    <p class="eyebrow big">PREPÁRATE</p>
    <p class="count${value === 'go' ? ' go' : ''}">${value === 'go' ? '¡Vamos!' : value}</p>
    <p class="muted small"${value === 'go' ? ' style="opacity:0"' : ''}>Toca para saltar</p>
  </div>`;
}

export function celebrationView(isNormal: boolean, streak: number): string {
  const colors = ['#30d158', '#0a84ff', '#ff9f0a', '#ff375f', '#ffd60a', '#bf5af2', '#ff453a'];
  const confetti = Array.from({ length: 44 }, () => {
    const r = (a: number, b: number) => a + Math.random() * (b - a);
    return `<i style="left:${r(0, 100).toFixed(1)}%;background:${colors[Math.floor(Math.random() * colors.length)]};--r0:${r(0, 360).toFixed(0)}deg;--r1:${r(180, 900).toFixed(0)}deg;animation-duration:${r(1.6, 3).toFixed(2)}s;animation-delay:${r(0, 0.6).toFixed(2)}s"></i>`;
  }).join('');
  return `<div class="overlay celebration">
    <div class="confetti" aria-hidden="true">${confetti}</div>
    <div class="spacer"></div>
    <div class="seal-wrap"><span class="glow"></span>${icons.sealCheck}</div>
    <h1 class="pop">${isNormal ? '¡Entreno completado!' : '¡Suelo hecho!'}</h1>
    <p class="muted big pop">${isNormal ? 'Muy bien hecho.' : 'Has aparecido, que es lo que cuenta.'}</p>
    <p class="streak pop">${icons.flame}<b>${streak === 1 ? '1 día seguido' : `${streak} días seguidos`}</b></p>
    <div class="spacer"></div>
    <p class="tomorrow pop">Vuelve mañana.</p>
    ${btn('Continuar', 'proceedToFeedback')}
  </div>`;
}

export const roundFlashView = () =>
  `<div class="overlay flash" aria-live="polite"><div class="flash-box">${icons.checkCircle}<p>¡Ronda completada!</p></div></div>`;

// ---------------------------------------------------------------------------------------------------------------
// Hojas (modales)

const sheet = (title: string, body: string, closable = true) => `<div class="sheet-backdrop"><div class="sheet" role="dialog" aria-label="${esc(title)}">
  <header class="sheet-head"><span></span><h3>${esc(title)}</h3>${closable ? `<button class="icon-button" data-act="closeModal" aria-label="Cerrar">${icons.close}</button>` : '<span></span>'}</header>
  <div class="sheet-body">${body}</div></div></div>`;

const scale = (from: number, to: number, selected: number | null, act: string, cols: number) =>
  `<div class="scale" style="--cols:${cols}">${Array.from({ length: to - from + 1 }, (_, i) => from + i)
    .map(v => `<button class="pick${selected === v ? ' selected' : ''}" data-act="${act}" data-arg="${v}">${v}</button>`).join('')}</div>`;

const yesNo = (selected: boolean | null, act: string) =>
  `<div class="pair">${[['No', 'false'], ['Sí', 'true']].map(([l, v]) => `<button class="pick${String(selected) === v ? ' selected' : ''}" data-act="${act}" data-arg="${v}">${l}</button>`).join('')}</div>`;

export interface FeedbackForm { effort: number | null; activity: string; pain: boolean | null; painNote: string; note: string }
export const ACTIVITIES = ['Ninguna', 'Escalada vías', 'Boulder', 'Wing Chun', 'Otra'];

export function feedbackSheet(f: FeedbackForm): string {
  return sheet('Cerrar el día', `
    <p class="muted">El entrenamiento cuenta como un día cumplido. Registra cómo te ha sentado.</p>
    <h4>Esfuerzo general al terminar</h4>${scale(1, 10, f.effort, 'fbEffort', 5)}
    <h4>Actividad prevista hoy (puedes cambiarla luego)</h4>
    <div class="chips">${ACTIVITIES.map(a => `<button class="pick${f.activity === a ? ' selected' : ''}" data-act="fbActivity" data-arg="${esc(a)}">${esc(a)}</button>`).join('')}</div>
    <h4>¿Dolor articular o molestia rara?</h4>${yesNo(f.pain, 'fbPain')}
    ${f.pain ? `<input class="field" data-field="painNote" placeholder="¿Dónde / qué notas?" value="${esc(f.painNote)}">` : ''}
    <h4>Nota opcional</h4>
    <textarea class="field" data-field="note" rows="3" placeholder="Ej.: dead bug sigue costando; hoy estaba fresco…">${esc(f.note)}</textarea>
    ${btn('Guardar día', 'saveFeedback', 'primary', '', f.effort === null ? ' disabled' : '')}`, false);
}

export interface RecoveryForm { session: Session; soreness: number | null; wantToReturn: boolean | null }

export function recoverySheet(r: RecoveryForm): string {
  return sheet('Después del suelo', `
    <p class="muted">Ya has hecho el suelo. Ahora que te has movido un poco, ¿cómo notas las agujetas del ${shortDate(r.session.date)}? Sirve para recalibrar el sistema.</p>
    <h4>Agujetas / carga que notas tras el suelo</h4>${scale(0, 10, r.soreness, 'recSoreness', 6)}
    <h4>¿Tienes ganas de volver a hacerlo hoy?</h4>${yesNo(r.wantToReturn, 'recWant')}
    ${btn('Guardar recuperación', 'saveRecovery', 'primary', '', r.soreness === null || r.wantToReturn === null ? ' disabled' : '')}`, false);
}

export interface PracticeForm {
  form: string; source: WingChunSource; countsAsMinimum: boolean; average: number | null;
  startedAt: number | null; step: 'timing' | 'done'; timed: boolean; result: PracticeResult | null; outcome: string; note: string;
}

export function practiceSheet(p: PracticeForm): string {
  const name = esc(formName(p.form));
  if (p.step === 'timing') {
    return sheet('Wing Chun', `<div class="practice">
      <span class="practice-icon">${icons.martialArts}</span>
      <h2>${name}</h2>
      <p class="muted">Nivel ${formLevel(p.form)} · ${p.countsAsMinimum ? 'cuenta como mínimo de hoy' : 'práctica extra'}</p>
      ${p.average ? `<p class="muted small">Tu media: ${clock(p.average)}</p>` : ''}
      <p class="huge-clock" data-bind="practice">00:00</p>
      ${p.startedAt ? btn('Terminar', 'practiceFinish') : btn('Empezar', 'practiceStart') + btn('Marcar hecha sin cronometrar', 'practiceQuick', 'secondary')}
    </div>`, true);
  }
  const head = p.result?.kind === 'minimum' ? 'Wing Chun completado' : `✓ ${name} completada`;
  const sub = p.result?.kind === 'minimum' ? (p.result.streak > 0 ? `Racha: ${plural(p.result.streak, 'día', 'días')}` : '')
    : p.result ? plural(p.result.count, 'práctica', 'prácticas') + ' hoy' : '';
  return sheet('Wing Chun', `<div class="practice">
    <span class="practice-icon ok">${icons.sealCheck}</span>
    <h2>${head}</h2>${sub ? `<p class="muted">${sub}</p>` : ''}
    ${p.timed ? `<section class="card inset">
      <h4>¿Qué tal ha ido?</h4>
      <div class="segmented">${OUTCOMES.map(o => `<button class="${p.outcome === o.id ? 'selected' : ''}" data-act="practiceOutcome" data-arg="${o.id}">${o.label}</button>`).join('')}</div>
      <input class="field" data-field="note" placeholder="Nota (opcional)" value="${esc(p.note)}">
      <p class="muted small">Opcional, para revisar con tu profesor. No afecta a la racha.</p>
    </section>` : ''}
    ${p.timed ? btn('Guardar', 'practiceSave') + btn('Ahora no', 'practiceClose', 'secondary') : btn('Hecho', 'practiceClose')}
  </div>`, false);
}

export function formPickerSheet(forms: string[], suggested: string, extra: boolean, average: (f: string) => number | null): string {
  return sheet(extra ? 'Practicar algo más' : 'Elegir forma', `
    <p class="muted">${extra ? 'Elige la forma que quieres practicar.' : 'Cualquiera de estas cumple el mínimo de hoy (Siu Nim Tau siempre vale).'}</p>
    <div class="list">${forms.map(id => {
      const f = WING_CHUN_FORMS.find(x => x.id === id);
      const avg = average(id);
      return `<button class="list-row" data-act="pickedForm" data-arg="${id}">
        <span><b>${esc(formName(id))}</b>${id === suggested ? ' <span class="tag">hoy toca</span>' : ''}<br>
        <small class="muted">Nivel ${formLevel(id)}${f ? ` · ${esc(f.requirement)}` : ''}${avg ? ` · tu media ${clock(avg)}` : ''}</small></span>${icons.play}</button>`;
    }).join('')}</div>`);
}

export interface SettingsInfo {
  email: string; workbookName: string; syncedAt: string; fromCache: boolean; pending: number; warnings: string[];
  rotation: string[]; floor: Exercise[]; normal: Exercise[]; maxRounds: number; ceilingMinutes: number; isIOS: boolean;
  pushSupported: boolean; reminderNote: string; reminderBusy: boolean;
}

export function settingsSheet(p: Prefs, s: SettingsInfo): string {
  const toggle = (label: string, on: boolean, act: string, hint = '') =>
    `<button class="toggle-row" data-act="${act}" aria-pressed="${on}"><span>${label}${hint ? `<br><small class="muted">${hint}</small>` : ''}</span><i class="switch${on ? ' on' : ''}"></i></button>`;
  const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');
  return sheet('Ajustes', `
    <h4>Entreno</h4>
    <div class="group">
      ${toggle('Cuenta atrás 3·2·1', p.countdownEnabled, 'togglePref', 'Antes de empezar las rondas.')}
      ${toggle('Última ronda guiada', p.measureLastRound, 'toggleMeasure', 'Ejercicio a ejercicio, con RIR.')}
    </div>
    <h4>Recordatorio diario</h4>
    <div class="group">
      ${toggle('Recordatorio', p.reminderEnabled, 'toggleReminder', s.pushSupported ? 'Un aviso al día a esta hora, con lo que te quede.' : 'En iPhone, abre la app desde su icono en la pantalla de inicio para activarlo.')}
      ${p.reminderEnabled ? `<div class="toggle-row"><span>Hora</span><input class="time" type="time" data-field="reminderTime" value="${esc(p.reminderTime)}"></div>
        <button class="toggle-row" data-act="testReminder"><span class="accent">Enviar un aviso de prueba</span></button>` : ''}
    </div>
    ${s.reminderBusy ? '<p class="small muted footnote">Un momento…</p>' : s.reminderNote ? `<p class="small footnote">${esc(s.reminderNote)}</p>` : ''}
    <h4>Tamaño del texto en las rondas</h4>
    <div class="segmented">${[[1, 'Normal'], [1.4, 'Grande'], [1.8, 'Enorme']].map(([v, l]) => `<button class="${p.textScale === v ? 'selected' : ''}" data-act="textScale" data-arg="${v}">${l}</button>`).join('')}</div>
    <h4>Sonido al terminar</h4>
    <div class="group">${CELEBRATION_SOUNDS.map(c => `<button class="toggle-row" data-act="celebrationSound" data-arg="${c.id}"><span>${esc(c.name)}</span>${p.celebrationSound === c.id ? icons.check : ''}</button>`).join('')}</div>
    <h4>Rutina</h4>
    <div class="group static">
      <p class="small muted">Suelo</p>${exerciseRows(s.floor)}
      <p class="small muted">Normal · ${s.maxRounds} rondas · techo ${s.ceilingMinutes} min</p>${exerciseRows(s.normal)}
      ${btn('Editar rutina', 'openRoutine', 'secondary')}
    </div>
    <h4>Wing Chun · formas en la rotación</h4>
    <div class="group">${WING_CHUN_FORMS.map(f => {
      const on = f.id === 'siu_nim_tau' || s.rotation.includes(f.id);
      return `<button class="toggle-row" data-act="toggleForm" data-arg="${f.id}"${f.id === 'siu_nim_tau' ? ' disabled' : ''}>
        <span>${esc(f.name)}<br><small class="muted">Nivel ${f.level} · ${esc(f.requirement)}${f.id === 'siu_nim_tau' ? ' · siempre (plan B)' : ''}</small></span><i class="switch${on ? ' on' : ''}"></i></button>`;
    }).join('')}</div>
    <h4>Libro</h4>
    <div class="group static">
      <p><b>${esc(s.workbookName)}</b></p>
      <p class="small muted">Última sincronización: ${fmt(s.syncedAt)}${s.fromCache ? ' (copia local)' : ''}${s.pending ? ` · ${plural(s.pending, 'cambio pendiente', 'cambios pendientes')}` : ''}</p>
      ${s.warnings.map(w => `<p class="small warn">${esc(w)}</p>`).join('')}
      ${btn('Sincronizar ahora', 'syncNow', 'secondary')}
      ${s.isIOS ? '' : btn('Cambiar de libro', 'changeWorkbook', 'secondary')}
    </div>
    <h4>Cuenta</h4>
    <div class="group static">
      <p class="small muted">${esc(s.email)}</p>
      ${btn('Cerrar sesión', 'logout', 'secondary danger')}
    </div>
    <p class="small muted center"><a href="/privacidad/">Privacidad</a> · <a href="/terminos/">Condiciones</a></p>`);
}

// ---------------------------------------------------------------------------------------------------------------
// Editor de rutina (se guarda entero al pulsar «Guardar»: pestaña Rutina + MAX_ROUNDS y CEILING_MINUTES en Settings)

export interface RoutineDraft { rows: Exercise[]; maxRounds: number; ceilingMinutes: number; dirty: boolean }

const MEASURES: Array<[Measure, string]> = [['reps', 'Reps'], ['reps_side', 'Por lado'], ['seconds', 'Segundos']];

const stepper = (label: string, value: number, act: string, arg: string, unit = '', field = '') => `<div class="stepper">
  <span>${label}</span>
  <button class="step" data-act="${act}" data-arg="${arg}|-1" aria-label="Menos">−</button>
  ${field ? `<input class="step-value" type="number" inputmode="numeric" min="1" data-field="${field}" value="${value}">` : `<b class="step-value">${value}</b>`}
  <button class="step" data-act="${act}" data-arg="${arg}|1" aria-label="Más">+</button>
  <small class="muted">${unit}</small>
</div>`;

export function routineSheet(d: RoutineDraft): string {
  const section = (sec: Section, title: string) => {
    const items = d.rows.map((e, i) => ({ e, i })).filter(x => x.e.section === sec);
    const active = items.filter(x => x.e.active);
    const archived = items.filter(x => !x.e.active);
    return `<h4>${title}</h4>
      ${active.map(({ e, i }, k) => `<section class="card edit-ex">
        <div class="edit-top">
          <input class="field name" data-field="name:${i}" value="${esc(e.name)}" placeholder="Nombre del ejercicio" enterkeyhint="done">
          <button class="mini" data-act="rtMove" data-arg="${i}|-1"${k === 0 ? ' disabled' : ''} aria-label="Subir">↑</button>
          <button class="mini" data-act="rtMove" data-arg="${i}|1"${k === active.length - 1 ? ' disabled' : ''} aria-label="Bajar">↓</button>
        </div>
        ${stepper('Cantidad', e.target, 'rtTarget', String(i), e.measure === 'seconds' ? 'segundos' : e.measure === 'reps_side' ? 'por lado' : 'repeticiones', `target:${i}`)}
        <div class="segmented">${MEASURES.map(([m, l]) => `<button class="${e.measure === m ? 'selected' : ''}" data-act="rtMeasure" data-arg="${i}|${m}">${l}</button>`).join('')}</div>
        ${sec === 'NORMAL' && e.measure !== 'seconds' ? `<div class="edit-q"><small class="muted">En la ronda guiada pregunta</small>
          <div class="segmented">${[['rir', 'RIR'], ['control', 'Control lumbar']].map(([q, l]) => `<button class="${e.question === q ? 'selected' : ''}" data-act="rtQuestion" data-arg="${i}|${q}">${l}</button>`).join('')}</div></div>` : ''}
        <button class="link danger" data-act="rtArchive" data-arg="${i}">Quitar de la rutina</button>
      </section>`).join('')}
      ${btn(`+ Añadir ejercicio ${sec === 'FLOOR' ? 'al suelo' : 'al normal'}`, 'rtAdd', 'secondary', sec)}
      ${archived.length ? `<p class="small muted archived-title">Quitados (se conservan para el historial)</p>
        <div class="group">${archived.map(({ e, i }) => `<button class="toggle-row" data-act="rtRestore" data-arg="${i}"><span>${esc(e.name)} <small class="muted">${esc(quantity(e))}</small></span><span class="accent">Recuperar</span></button>`).join('')}</div>` : ''}`;
  };
  return sheet('Rutina', `
    <p class="muted">Los cambios valen desde el próximo entreno. Las sesiones pasadas guardan su propia rutina.</p>
    <h4>Entreno normal</h4>
    <div class="group static">
      ${stepper('Rondas', d.maxRounds, 'rtRounds', 'r')}
      ${stepper('Techo', d.ceilingMinutes, 'rtCeiling', 'c', 'min')}
    </div>
    ${section('FLOOR', 'Suelo (el mínimo diario)')}
    ${section('NORMAL', 'Rondas del entreno normal')}
    ${btn('Guardar rutina', 'rtSave', 'primary', '', d.dirty ? '' : ' disabled')}
    ${btn('Cancelar', 'closeModal', 'secondary')}`);
}

// ---------------------------------------------------------------------------------------------------------------
// Revisión del bloque (texto para ChatGPT)

export function blockReviewSheet(text: string, copied: boolean): string {
  return sheet('Recalibración', `
    <p class="muted">Copia todo este texto y pégalo en ChatGPT junto con el fichero de Vuelve Mañana si estás en un chat nuevo.</p>
    <pre class="review-text">${esc(text)}</pre>
    ${btn(copied ? 'Copiado ✓' : 'Copiar', 'copyBlockReview')}
    ${btn('Copiar y marcar como revisado', 'markBlockReviewed', 'secondary')}
    ${btn('Cerrar sin marcar', 'closeModal', 'secondary')}`);
}

// ---------------------------------------------------------------------------------------------------------------
// Historial

const longDate = (key: string) => key.split('-').reverse().join('/');
const days = (n: number) => `${n} ${n === 1 ? 'día' : 'días'}`;
const kv = (k: string, v: string, cls = '') => `<div class="kv"><span>${k}</span><b class="${cls}">${v}</b></div>`;

function consistency(st: HabitStats): string {
  const pct = recoveryPercent(st);
  return `<h4>Consistencia</h4><div class="group static">
    ${kv('Recuperación tras fallo', pct === null ? 'Nunca has fallado dos veces' : `${st.recovered}/${st.recoveryOpportunities} · ${pct}%`, pct === null ? 'ok-text' : '')}
    ${kv('Últimos 7 días', `${st.last7}/7`)}
    ${kv('Últimos 30 días', `${st.last30}/30 · ${Math.round((st.last30 / 30) * 100)}%`)}
    ${kv('Racha actual', days(st.currentStreak))}
    ${kv('Mejor racha', days(st.bestStreak))}
  </div><p class="small muted footnote">Recuperación: de las veces que fallaste un día, cuántas volviste al día siguiente. Nunca falles dos veces.</p>`;
}

export function historySheet(wb: Workbook, tab: 'workout' | 'wingChun', copied: boolean): string {
  const seg = `<div class="segmented history-tabs">${[['workout', 'Entreno'], ['wingChun', 'Wing Chun']].map(([id, l]) =>
    `<button class="${tab === id ? 'selected' : ''}" data-act="historyTab" data-arg="${id}">${l}</button>`).join('')}</div>`;
  if (tab === 'workout') {
    const sessions = [...wb.sessions].sort((a, b) => (b.date + (b.floorCompletedAt || '')).localeCompare(a.date + (a.floorCompletedAt || '')));
    const p = blockProgress(wb);
    const b = blockSummary(wb.sessions, p.base);
    return sheet('Historial', `${seg}
      ${sessions.length ? consistency(habitStats(new Set(wb.sessions.map(x => x.date).filter(Boolean)))) : ''}
      ${b.days ? `<h4>Bloque ${p.blockNumber} (actual)</h4><div class="group static">
        ${kv('Días con entreno normal', `${b.normals}/${b.days} · ${b.normalPercent}%`)}
        ${b.averageEffort !== null ? kv('Esfuerzo medio', `${b.averageEffort.toFixed(1)}/10`) : ''}
        ${b.averageSoreness !== null ? kv('Agujetas medias', `${b.averageSoreness.toFixed(1)}/10`) : ''}
      </div>` : ''}
      <h4>Sesiones</h4>
      <div class="list">${sessions.length ? sessions.map(x => `<button class="list-row" data-act="editSession" data-arg="${esc(x.id)}"${x.id ? '' : ' disabled'}>
        <span><b>${longDate(x.date)}</b>
          <br><small class="muted">${[x.sessionType === 'NORMAL' ? `${x.rounds} ${x.rounds === 1 ? 'ronda' : 'rondas'} · ${clock(x.durationSeconds)}` : '',
          x.effort !== null ? `esfuerzo ${x.effort}/10` : '', x.nextDaySoreness !== null ? `agujetas ${x.nextDaySoreness}/10` : '',
          x.activity && x.activity !== 'Ninguna' ? esc(x.activity) : ''].filter(Boolean).join(' · ') || 'Sin cuestionario'}</small></span>
        <span class="type ${x.sessionType === 'NORMAL' ? 'ok-text' : 'muted'}">${x.sessionType === 'NORMAL' ? 'Normal' : 'Suelo'}</span></button>`).join('')
        : '<p class="muted pad">Todavía no hay sesiones.</p>'}</div>`);
  }
  const wc = wingChunToday(wb);
  const r = wingChunReviewProgress(wb);
  const byDay = new Map<string, WingChunPractice[]>();
  for (const x of [...wb.wingChun].sort((a, b) => b.completedAt.localeCompare(a.completedAt))) byDay.set(x.date, [...(byDay.get(x.date) ?? []), x]);
  const aggs = formAggregates(wb.wingChun);
  return sheet('Historial', `${seg}
    ${consistency(wc.stats())}
    <h4>Revisión Wing Chun</h4><div class="group static">
      ${kv(r.isDue ? 'Estado' : 'Progreso', r.isDue ? 'Lista con ChatGPT' : `${r.elapsed}/${r.target} días`)}
      ${btn(copied ? '¡Copiada! ✓' : 'Copiar revisión', 'copyWingChunReview', 'secondary', '', wb.wingChun.length ? '' : ' disabled')}
    </div>
    ${aggs.length ? `<h4>Por forma</h4><div class="group static">${aggs.map(a => kv(esc(a.name), `${a.count} · ${totalText(a.totalSeconds)}`)).join('')}</div>` : ''}
    ${byDay.size ? [...byDay].map(([day, xs]) => `<h4 class="day-head"><span>${longDate(day)}</span><small class="muted">${xs.length} · ${totalText(xs.reduce((a, x) => a + (x.durationSeconds ?? 0), 0))}</small></h4>
      <div class="list">${xs.map(x => `<button class="list-row" data-act="editPractice" data-arg="${esc(x.id)}">
        <span><b>${esc(formName(x.form))}</b><br><small class="${x.isDailyMinimum ? 'ok-text' : 'muted'}">${x.isDailyMinimum ? 'Diaria' : 'Extra'}</small>${x.outcome ? `<small class="muted"> · ${esc(OUTCOMES.find(o => o.id === x.outcome)?.label ?? x.outcome)}</small>` : ''}${x.note ? `<br><small class="muted">${esc(x.note)}</small>` : ''}</span>
        <span class="muted num">${x.durationSeconds !== null ? clock(x.durationSeconds) : '—'}</span></button>`).join('')}</div>`).join('')
      : '<h4>Días</h4><p class="muted">Todavía no hay práctica de Wing Chun.</p>'}`);
}

// ---------------------------------------------------------------------------------------------------------------
// Editar un día de Fuerza

export interface SessionForm {
  original: Session; effort: number | null; activity: string; pain: boolean | null; painNote: string; note: string;
  soreness: number | null; wantToReturn: boolean | null; answers: Record<string, MeasurementCode>; confirmDelete: boolean;
}

export function sessionSheet(f: SessionForm, config: WorkoutConfig): string {
  const s = f.original;
  const measured = Object.keys(decodeFeedback(s.exerciseFeedback)).length > 0;
  const choicesFor = (q: string): MeasurementCode[] => (q === 'done' ? ['done'] : q === 'control' ? [...CONTROL_CHOICES] : [...RIR_CHOICES]).concat(q === 'done' ? ['skipped'] : ['unknown', 'skipped']);
  return sheet(longDate(s.date), `
    <div class="group static">
      ${kv('Tipo', s.sessionType === 'NORMAL' ? 'Normal' : 'Suelo')}
      ${s.sessionType === 'NORMAL' ? kv('Rondas', hasIncompleteGuidedRound(s, config) ? `${s.rounds} + última incompleta` : String(s.rounds)) + kv('Duración', clock(s.durationSeconds)) : ''}
      ${s.warmupSeconds !== null ? kv('Calentamiento', clock(s.warmupSeconds)) : ''}
    </div>
    ${measured ? `<h4>Última ronda</h4><div class="group static">${measuredExercises(s, config).map(e => {
      const q = e.measure === 'seconds' ? 'done' : e.question;
      return `<div class="answer-row"><span>${esc(e.name)}</span><div class="mini-choices">${choicesFor(q).map(c =>
        `<button class="pick${f.answers[e.id] === c ? ' selected' : ''}" data-act="sdAnswer" data-arg="${esc(e.id)}|${c}">${esc(c === 'unknown' ? '?' : c === 'skipped' ? 'Saltado' : c === 'done' ? 'Hecho' : choiceLabel(c))}</button>`).join('')}</div></div>`;
    }).join('')}</div><p class="small muted footnote">«?» = hecho sin valorar. «Saltado» = no realizado.</p>` : ''}
    <h4>Actividad</h4>
    <div class="chips">${ACTIVITIES.map(a => `<button class="pick${f.activity === a ? ' selected' : ''}" data-act="sdActivity" data-arg="${esc(a)}">${esc(a)}</button>`).join('')}</div>
    <h4>Esfuerzo general</h4>${scale(1, 10, f.effort, 'sdEffort', 5)}
    <h4>Molestias</h4>${yesNo(f.pain, 'sdPain')}
    ${f.pain ? `<input class="field" data-field="sdPainNote" placeholder="¿Dónde / qué notas?" value="${esc(f.painNote)}">` : ''}
    <h4>Nota</h4><textarea class="field" data-field="sdNote" rows="3" placeholder="Nota opcional">${esc(f.note)}</textarea>
    <h4>Recuperación (día siguiente)</h4>
    <p class="small muted">Agujetas / carga</p>${scale(0, 10, f.soreness, 'sdSoreness', 6)}
    <p class="small muted" style="margin-top:10px">¿Ganas de volver?</p>${yesNo(f.wantToReturn, 'sdWant')}
    ${btn('Guardar', 'sdSave')}
    ${btn(f.confirmDelete ? 'Toca otra vez para eliminar (no se puede deshacer)' : 'Eliminar sesión', 'sdDelete', 'secondary danger')}`);
}

export interface PracticeEditForm { original: WingChunPractice; outcome: string; note: string; confirmDelete: boolean }

export function practiceEditSheet(f: PracticeEditForm): string {
  const x = f.original;
  return sheet(formName(x.form), `
    <div class="group static">
      ${kv('Fecha', longDate(x.date))}
      ${kv('Duración', x.durationSeconds !== null ? clock(x.durationSeconds) : 'Sin cronometrar')}
      ${kv('Cuenta como', x.isDailyMinimum ? 'Mínimo del día' : 'Práctica extra')}
    </div>
    <h4>¿Qué tal ha ido?</h4>
    <div class="segmented">${OUTCOMES.map(o => `<button class="${f.outcome === o.id ? 'selected' : ''}" data-act="peOutcome" data-arg="${o.id}">${o.label}</button>`).join('')}</div>
    <input class="field" data-field="peNote" placeholder="Nota (opcional)" value="${esc(f.note)}">
    ${btn('Guardar', 'peSave')}
    ${btn(f.confirmDelete ? 'Toca otra vez para eliminar (no se puede deshacer)' : 'Eliminar práctica', 'peDelete', 'secondary danger')}`);
}
